import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, BookOpenIcon, TargetIcon, HomeIcon, LightbulbIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReviewsGallery } from '../components/ReviewsGallery';
import { EnrollmentOffer } from '../components/EnrollmentOffer';
import { FAQSection } from '../components/FAQSection';
export function Course1Page() {
  const modules = [{
    id: '01',
    title: 'Etapele reale de dezvoltare',
    icon: <BookOpenIcon size={48} className="text-brand-gold" data-id="element-515" />,
    bullets: ['Ce ar trebui să facă un copil la 1 an, la 2 ani, la 3 ani și la 4 ani pe fiecare arie de dezvoltare', 'De ce reperele de pe internet sunt adesea incomplete sau înșelătoare', 'Cum să evaluezi singură unde e copilul tău față de aceste repere']
  }, {
    id: '02',
    title: 'Variație normală versus întârziere',
    icon: <TargetIcon size={48} className="text-brand-gold" data-id="element-516" />,
    bullets: ['Care e diferența dintre un copil care vorbește mai târziu și unul care are o întârziere reală', 'Cum să recunoști semnele de alarmă fără să te panichezi', 'Ce înseamnă "să mai așteptăm" și când așteptarea devine o problemă']
  }, {
    id: '03',
    title: 'Rolul mediului și al rutinelor',
    icon: <HomeIcon size={48} className="text-brand-gold" data-id="element-517" />,
    bullets: ['Cum influențează mediul de acasă dezvoltarea copilului tău', 'Ce efect au ecranele asupra limbajului și atenției la vârste mici', 'Cum să creezi un mediu care stimulează dezvoltarea în mod natural']
  }, {
    id: '04',
    title: 'Primii pași practici',
    icon: <LightbulbIcon size={48} className="text-brand-gold" data-id="element-518" />,
    bullets: ['Primele tehnici simple de stimulare a limbajului pe care le poți aplica imediat', 'Cum să transformi rutinele zilnice în momente de învățare', 'Când e suficient să lucrezi acasă și când e momentul să ceri ajutor profesional']
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-[64px] md:pt-[72px]" data-id="element-519">
      {/* SECȚIUNEA 1 — HERO OFERTĂ */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-520">
        <div className="container-custom" data-id="element-521">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-522">
            <div className="max-w-xl" data-id="element-523">
              <h1 className="font-playfair text-[48px] md:text-[64px] font-bold text-white mb-6 leading-[1.1]" data-id="element-524">
                Cunoașterea Dezvoltării
              </h1>
              <p className="font-source text-[20px] md:text-[24px] text-brand-gold mb-10 italic" data-id="element-525">
                Înțelege cum se dezvoltă cu adevărat copilul tău și știi exact
                ce e normal și ce necesită atenție.
              </p>
              <a href="#inscriere" className="btn-inverted text-[18px] px-8 py-4" data-id="element-526">
                Click aici pentru înscriere!
              </a>
            </div>
            <div className="relative rounded-[24px] overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-527">
              <img src="https://assets.cdn.filesafe.space/3SQqBp51ZHIpTGzJ3Vu1/media/6a2bf333fc5dba9abbf7a5d3.jpg" alt="Cunoașterea Dezvoltării" className="w-full h-full object-cover aspect-[4/3]" data-id="element-528" />
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 2 — DESCRIERE */}
      <section className="bg-brand-beige section-padding" data-id="element-529">
        <div className="container-custom" data-id="element-530">
          <div className="max-w-3xl mx-auto" data-id="element-531">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy mb-8 text-center" data-id="element-532">
              Nu știi dacă copilul tău se dezvoltă normal sau dacă trebuie să
              acționezi acum?
            </h2>
            <div className="font-source text-[16px] md:text-[18px] text-brand-text space-y-6" data-id="element-533">
              <p data-id="element-534">
                Ești mama unui copil mic și ceva nu ți se pare în regulă. Poate
                nu vorbește încă. Poate face lucruri pe care alți copii de
                vârsta lui nu le fac. Ai căutat pe Google, ai citit pe grupuri
                de Facebook, ai întrebat pe cine ai putut. Unii îți spun că
                exagerezi, alții te sperie. Nu ai un reper clar pe care să te
                bazezi și asta te ține într-o stare constantă de incertitudine.
              </p>
              <p data-id="element-535">
                Fără informația corectă, riști să aștepți prea mult sau să te
                panichezi inutil. Amândouă te costă: fie pierzi luni prețioase
                în care ai fi putut face diferența, fie cheltuiești bani și
                energie pe soluții care nu se potrivesc copilului tău.
              </p>
              <div className="bg-white p-6 rounded-[16px] border-l-4 border-brand-coral" data-id="element-536">
                <p className="font-medium text-brand-navy" data-id="element-537">
                  Cursul "C - Cunoașterea Dezvoltării" îți dă exact reperii de
                  care ai nevoie. Înveți cum arată dezvoltarea normală la
                  fiecare vârstă, care sunt semnele de alarmă reale și cum să
                  faci diferența dintre o variație normală și o întârziere care
                  necesită atenție. Nu din cărți, ci din experiența directă a
                  unui psiholog clinician cu peste 11 ani de lucru cu copii
                  mici.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 3B — CE VEI DESCOPERI */}
      <section className="bg-brand-ivory section-padding" data-id="element-538">
        <div className="container-custom" data-id="element-539">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-16" data-id="element-540">
            Ce vei descoperi concret în Cursul 1: Cunoașterea Dezvoltării
          </h2>
          <div className="max-w-4xl mx-auto space-y-8" data-id="element-541">
            {modules.map(mod => <div key={mod.id} className="flex flex-col md:flex-row bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden p-8 gap-8 items-start" data-id="element-542">
                {/* Icon Side */}
                <div className="flex-shrink-0 w-24 h-24 rounded-full bg-brand-beige flex items-center justify-center" data-id="element-543">
                  {mod.icon}
                </div>

                {/* Content Side */}
                <div className="flex-grow" data-id="element-544">
                  <h3 className="font-playfair text-[24px] md:text-[28px] font-bold text-brand-navy mb-6 leading-[1.2]" data-id="element-545">
                    {mod.title}
                  </h3>
                  <ul className="space-y-4 flex flex-col" data-id="element-546">
                    {mod.bullets.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-547">
                        <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-1" size={20} data-id="element-548" />
                        <span className="font-source text-[15px] text-brand-text" data-id="element-549">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 4 — BENEFICII */}
      <section className="bg-brand-beige section-padding" data-id="element-550">
        <div className="container-custom" data-id="element-551">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-552">
            Ce se schimbă după ce parcurgi acest curs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-553">
            {[{
            title: 'Știi exact unde e copilul tău',
            desc: 'Nu te mai bazezi pe păreri de pe internet sau pe sfaturi de la apropiați. Ai repere clare și știi exact ce e normal la vârsta copilului tău și ce necesită atenție.'
          }, {
            title: 'Nu te mai sperii la fiecare articol citit pe internet',
            desc: 'Înțelegi diferența dintre o variație normală și o întârziere reală. Poți filtra informațiile și te bazezi pe cunoștințe, nu pe emoții.'
          }, {
            title: 'Știi ce să faci acasă, chiar de azi',
            desc: 'Pleci din curs cu primele tehnici practice de stimulare pe care le poți aplica imediat, în joc, la masă sau în plimbare.'
          }, {
            title: 'Știi când să ceri ajutor profesional',
            desc: 'Nu mai amâni o evaluare de care copilul are nevoie și nu mai cheltuiești bani pe intervenții care nu se potrivesc.'
          }].map((card, i) => <div key={i} className="bg-white p-6 rounded-[16px] shadow-sm border-t-4 border-brand-gold" data-id="element-554">
                <h3 className="font-montserrat font-bold text-[16px] text-brand-navy mb-3" data-id="element-555">
                  {card.title}
                </h3>
                <p className="font-source text-[14px] text-brand-text" data-id="element-556">
                  {card.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 5 — CINE SUNT EU */}
      <section className="bg-brand-ivory section-padding" data-id="element-557">
        <div className="container-custom" data-id="element-558">
          <div className="max-w-4xl mx-auto" data-id="element-559">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-10" data-id="element-560">
              Cine sunt eu și de ce am creat acest curs
            </h2>
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-8 items-stretch" data-id="element-561">
              <div className="md:col-span-2 font-source text-[16px] md:text-[18px] text-brand-text space-y-4 text-center md:text-left flex flex-col justify-center" data-id="element-562">
                <p data-id="element-563">
                  Sunt Cristina Geană, psiholog clinician cu peste 11 ani de
                  experiență directă cu copii mici. Am evaluat sute de copii și
                  am văzut de prea multe ori mame care pierd luni prețioase
                  pentru că nu au avut pe cineva care să le spună clar ce e
                  normal și ce nu.
                </p>
                <p data-id="element-564">
                  Am creat acest curs pentru că vreau ca orice mamă să aibă
                  acces la informația pe care eu o dau în cabinetul meu,
                  structurată simplu și pe înțelesul oricui.
                </p>
              </div>
              <div className="md:col-span-1" data-id="element-565">
                <img src="https://cdn.magicpatterns.com/uploads/wvUBHAy6VhqaRqNiVJPB6X/Cristina_poze_%285%29.jpg" alt="Cristina Geană" className="w-full h-full object-cover object-top rounded-[24px] shadow-card" data-id="element-566" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 6 — PENTRU CINE ESTE / NU ESTE */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-567">
        <div className="container-custom" data-id="element-568">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white text-center mb-12" data-id="element-569">
            Pentru cine este acest curs?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-570">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-[24px] border border-white/20" data-id="element-571">
              <h3 className="font-playfair text-[28px] font-bold text-brand-gold mb-6 !text-brand-gold" data-id="element-572">
                Pentru cine este
              </h3>
              <ul className="space-y-4" data-id="element-573">
                {['Mame de copii între 1 și 4 ani care nu sunt sigure dacă dezvoltarea copilului lor e în parametri normali', 'Mame care au observat diferențe față de alți copii de aceeași vârstă dar nu știu dacă e motiv de îngrijorare', 'Mame de copii supraexpuși la ecrane care vor să înțeleagă ce efect au avut și ce pot face'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-574">
                    <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-0.5" size={20} data-id="element-575" />
                    <span className="font-source text-[15px] text-white/90" data-id="element-576">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white/5 p-8 rounded-[24px] border border-white/10" data-id="element-577">
              <h3 className="font-playfair text-[28px] font-bold text-white mb-6 !text-white" data-id="element-578">
                Pentru cine NU este
              </h3>
              <ul className="space-y-4" data-id="element-579">
                {['Mame care caută o rețetă magică și rapidă fără să fie dispuse să observe și să lucreze cu copilul', 'Mame care sunt convinse că nu e nicio problemă și caută doar confirmare', 'Profesioniști care caută un curs de specialitate. Acest curs e pentru mame, în limbaj accesibil'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-580">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-581">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60" data-id="element-582"></span>
                    </div>
                    <span className="font-source text-[15px] text-white/70" data-id="element-583">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 7 — OFERTĂ */}
      <EnrollmentOffer title="Ce primești și care este investiția ta" includes={['Acces pe viață la Modulul 1 - Cunoașterea Dezvoltării', 'Materiale video structurate pas cu pas', 'Tehnici practice de aplicat acasă', 'Informație clară, fără termeni medicali']} price="395 lei" note="Acest modul face parte din Colecția Completă Uite cum C.R.E.S.C., dar poate fi achiziționat și separat." data-id="element-584" />

      {/* SECȚIUNEA 8 — TESTIMONIALE */}
      <section className="bg-brand-ivory section-padding" data-id="element-585">
        <div className="container-custom" data-id="element-586">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-587">
            Ce spun mamele care deja au lucrat cu mine
          </h2>
          <ReviewsGallery data-id="element-588" />
        </div>
      </section>

      {/* SECȚIUNEA 9 — FAQ */}
      <FAQSection faqs={[{
      q: 'Când primesc acces la curs?',
      a: 'Imediat după confirmarea plății, vei primi pe email datele de acces la platforma unde se află cursul.'
    }, {
      q: 'Cât timp am acces la materiale?',
      a: 'Ai acces pe viață la acest modul. Îl poți revedea ori de câte ori ai nevoie, în ritmul tău.'
    }, {
      q: 'Pot cumpăra și restul modulelor mai târziu?',
      a: 'Da, poți achiziționa celelalte module separat sau poți opta pentru Colecția Completă C.R.E.S.C. pentru a beneficia de prețul special și de apelurile de grup.'
    }]} data-id="element-589" />

      {/* SECȚIUNEA 10 — ÎNSCRIERE */}
      <section id="inscriere" className="bg-brand-navy section-padding text-center text-white" data-id="element-590">
        <div className="container-custom" data-id="element-591">
          <div className="max-w-3xl mx-auto" data-id="element-592">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white mb-6" data-id="element-593">
              Înscriere Modulul 1 - Cunoașterea Dezvoltării
            </h2>
            <p className="font-source text-[18px] text-white/80 mb-8" data-id="element-594">
              Completează formularul de mai jos pentru a finaliza înscrierea.
            </p>

            <div className="bg-white rounded-[24px] overflow-hidden min-h-[600px]" data-id="element-595">
              <iframe src="https://api.leadconnectorhq.com/widget/form/diR539HXcP2SHxIYrNRX" style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none'
            }} id="inline-diR539HXcP2SHxIYrNRX" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form Modul 1" data-height="600" data-layout-iframe-id="inline-diR539HXcP2SHxIYrNRX" data-form-id="diR539HXcP2SHxIYrNRX" title="Form Modul 1" data-id="element-596" />
            </div>
          </div>
        </div>
      </section>
    </motion.div>;
}